import React, { useState, forwardRef, useImperativeHandle } from 'react';
import BezierEasing from 'bezier-easing';
import { useSpring, animated } from 'react-spring';

const RIPPLE_STYLE = {
  position: 'absolute' as const,
  backgroundColor: 'rgba(0, 0, 0, 0.24)',
  borderRadius: '100%',
  zIndex: 0,
  transform: 'translate(-50%, -50%)'
};

export type DeprecatedRippleEffectHandle = {
  handleKeyDown: (any) => void,
  handleMouseDown: (any) => void,
};

export interface DeprecatedRippleEffectProps extends React.ComponentPropsWithoutRef<'div'> {
  style?: React.CSSProperties;
  rippleStyle?: React.CSSProperties;
  children: React.ReactNode;
  disabled?: boolean;
  center?: boolean;
  as?: string;
  wrapperRef?: React.RefObject<HTMLElement>;
  onMouseDown?: React.MouseEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
}

const DeprecatedRippleEffect = forwardRef<DeprecatedRippleEffectHandle, DeprecatedRippleEffectProps>((props: DeprecatedRippleEffectProps, ref) => {
  const {
    disabled,
    style,
    children,
    center,
    as,
    rippleStyle,
    wrapperRef,
    onMouseDown,
    onKeyDown,
    ...otherProps
  } = props;

  const [rippleConfig, setRippleConfig] = useState<{ style: React.CSSProperties, done?: boolean }>({ style: {} });
  // @ts-expect-error: This might be fixed after updating react-spring
  const [animationProps, set, stop] = useSpring(() => ({
    reset: true,
    config: {
      easing: BezierEasing(0, 0, 1, 0.5),
      duration: 400,
    },
    from: { width: 0, height: 0, opacity: 1 }
  }));

  const handleMouseDown = (event) => {
    if (event.button !== 0 || disabled) {
      onMouseDown && onMouseDown(event);
      return;
    }
    // @ts-expect-error: This might be fixed after updating react-spring
    stop();
    const { pageX, pageY, currentTarget } = event;
    const rect = currentTarget.getBoundingClientRect();
    const left = center ? rect.width / 2 : pageX - (rect.left + window.scrollX);
    const top = center ? rect.height / 2 : pageY - (rect.top + window.scrollY);
    // @ts-expect-error: This might be fixed after updating react-spring
    set({ width: rect.width * 2, height: rect.width * 2, opacity: 0 });
    setRippleConfig({
      style: {
        left,
        top,
      }
    });
    onMouseDown && onMouseDown(event);
  };

  const handleKeyDown = (event) => {
    if (disabled || !(event.keyCode === 13 || event.keyCode === 32)) {
      onKeyDown && onKeyDown(event);
      return;
    }
    // @ts-expect-error: This might be fixed after updating react-spring
    stop();
    const { currentTarget } = event;
    const rect = currentTarget.getBoundingClientRect();
    // @ts-expect-error: This might be fixed after updating react-spring
    set({ width: rect.width * 2, height: rect.width * 2, opacity: 0 });
    setRippleConfig({
      done: false,
      style: {
        left: rect.width / 2,
        top: rect.height / 2,
      }
    });
    onKeyDown && onKeyDown(event);
  };

  useImperativeHandle(ref, () => ({
    handleKeyDown,
    handleMouseDown,
  }));

  const wrapperProps = {
    style: {
      maxWidth: '100%',
      backgroundColor: 'transparent',
      position: 'relative' as const,
      display: 'inline-flex',
      overflow: 'hidden',
      height: 'fit-content',
      ...style,
    },
    onMouseDown: (event) => { handleMouseDown(event); },
    onKeyDown: (event) => { handleKeyDown(event); },
    ...otherProps
  };
  const Component = as;

  return (
    <Component {...wrapperProps}>
      {children}
      <animated.div
        style={{
          cursor: disabled ? 'default' : 'pointer',
          ...RIPPLE_STYLE,
          ...rippleStyle,
          ...rippleConfig.style,
          ...animationProps
        }}
      />
    </Component>
  );
});

DeprecatedRippleEffect.defaultProps = {
  rippleStyle: {},
  style: {},
  disabled: false,
  center: false,
  as: 'div',
  wrapperRef: {
    current: undefined
  },
  onKeyDown: undefined,
  onMouseDown: undefined
};
DeprecatedRippleEffect.displayName = 'DeprecatedRippleEffect';
export default DeprecatedRippleEffect;
